import React from 'react';
import './styles.css';
import './mobile.css';
import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView, WidgetContainer, WidgetView } from 'components/shared';
import { SecondaryButton } from 'components/ui';
import { FormContainer, FormRowView, TableColumnContainer, TableColumnView, TableItemTextView, TableItemView, TableView } from 'components/shared';
import CustomTableAccordion, { CustomTableContent, CustomTableToggle, useCustomTableAccordion } from 'components/custom-ui/CustomTableAccordion';
import { SVGAsset } from 'res';
import { BCMSRecord, Link, DisabledPrompt, useBCMSContext, useModel, useRoute, useStateManager, useWorkflow } from "bcms-react";
import MobileUnlinkedAccountsTable from './components/MobileUnlinkedAccountsTable';
import Classes, { UnlinkedAccount } from "model";
import CustomContentHeaderWidget from 'components/shared/views/CustomContentHeaderWidget';

/**
 * @function UnlinkedTransferAccountsPage
 * @return {React.Component}
 * @exported true
 * @description The page that shows the unlinked transfer accounts
 */

export default function UnlinkedTransferAccountsPage(props) {
  const { settings } = useModel();
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);
  const data = { ...getDefaultData(globalController), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui
  });

  return(
    <PageContainer className="unlinked-transfer-accounts-page" state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = (controller, _fiName, _accounts) => {
  const accounts = _accounts ?? UnlinkedAccount.initAll();
  const fiName = _fiName ?? controller.state.settings.fiName;

  return {
    accounts,
    fiName: fiName
  };
}

export const getDefaultUI = () => ({
  // no ui changes for now
});

function PageContent(props) {
  const { data, ui } = useStateManager();
  const accounts = data.accounts ?? [];
  const { branding } = useModel();
  const subHeaderContent = branding.options.transfer_accounts_subheader_abi22;
  const promoText = branding.options.transfer_accounts_promo_abi22;

  return (
    <PageContentView>
      <div className="flex-container flex-vertical page-widget-wrapper">
        {promoText && <CustomContentHeaderWidget content = {promoText}/>}

        <CenterContentContainer>
          <div className="upper-content">
            <ContentHeaderView text="Transfer Accounts" />
            {subHeaderContent && <CustomContentHeaderWidget content = {subHeaderContent}/>}
            <DisabledPrompt>
              <SecondaryButton text="Add Account" dataPoint="Add_Account_Button" />
            </DisabledPrompt>
            <h1>Unlinked { data.fiName } Accounts</h1>
          </div>
          <div className="show-on-desktop">
            <UnlinkedAccountsTable accounts={ accounts } />
          </div>
          <div className="show-on-mobile">
            <MobileUnlinkedAccountsTable accounts={ accounts } />
          </div>
        </CenterContentContainer>
      </div>
    </PageContentView>
  );
}

/**
 * @function UnlinkedAccountsTable
 * @return {React.Component}
 * @exported true
 * @description The unlinked accounts table
 */

function UnlinkedAccountsTable(props) {
  const accounts = props.accounts ?? [];
  const rows = accounts.reduce((acc, account) => {
    acc.push(
      <UnlinkedAccountsTableRow key={ account.id } account={ account } />
    );
    return acc;
  }, []);

  return(
    <TableView className="unlinked-accounts-table">
      <TableColumnContainer>
        <TableColumnView text="" /> 
        <TableColumnView text="Nickname" icon="ArrowUpIcon" />
        <TableColumnView text="Account Owner" icon="SortIcon" />
        <TableColumnView text="Account Owner Type" icon="SortIcon" />
        <TableColumnView text="Account Number" />
        <TableColumnView text="Date Added" icon="SortIcon" />
      </TableColumnContainer>
      { rows }
    </TableView>
  );
}

/**
 * @function UnlinkedAccountsTableRow
 * @return {React.Component}
 * @exported false
 * @description The table row in the unlinked account table
 */

function UnlinkedAccountsTableRow(props) {
  const account = props.account ?? {};
  const activeRow = account.activeRow ?? false;

  return(
      <CustomTableAccordion activeClass="unlinked-accounts-table-row-open" state={ activeRow }>
        <UnlinkedAccountsTableToggle account={ account } />
        <UnlinkedAccountsTableToggleContent account={ account } />
      </CustomTableAccordion>
  );
}

/**
 * @function UnlinkedAccountsTableToggle
 * @return {React.Component}
 * @exported false
 * @description description
 */

function UnlinkedAccountsTableToggle(props) {
  const { state } = useCustomTableAccordion();
  const account = props.account ?? {};

  return(
    <CustomTableToggle>
      <tr>
        <TableItemView className="expand-row-chevron">
          <SVGAsset id={ state ? "DownChevronIcon" : "RightChevronIcon"} data-point={ "Transfer_Chevron_" + account.id } />
        </TableItemView>
        <TableItemTextView text={ account.nickname } />
        <TableItemTextView text={ account.accountOwner } />
        <TableItemTextView className="account-owner-type-column" text={ account.accountOwnerType } />
        <TableItemTextView text={ account.number } />
        <TableItemTextView text={ account.dateOpened.format("m/d/Y") } />
      </tr>
    </CustomTableToggle>
  );
}

/**
 * @function UnlinkedAccountsTableToggleContent
 * @return {React.Component}
 * @exported false
 * @description The toggled content in the unlinked accounts table
 */

function UnlinkedAccountsTableToggleContent(props) {
  const account = props.account ?? {};

  return(
    <CustomTableContent>
    <tr>
        <td className="scheduled-transfers-table-item" colSpan="6">
            <div className="flex-container">
                <p className="flex-width">ACCOUNT DETAILS</p>         
                <CustomTableToggle>
                    <SVGAsset id="CloseX" className="set-width clickable" />
                </CustomTableToggle>          
            </div>

            <FormContainer>
                <FormRowView text="Date Added">
                    <p>{ account.dateOpened.format("m/d/Y") }</p>
                </FormRowView>
                
                <FormRowView className="link-container">
                    <DisabledPrompt>
                        <Link>Edit Account</Link>
                    </DisabledPrompt>
                </FormRowView>
                
                <FormRowView className="link-container">
                    <DisabledPrompt>
                        <Link>Remove Account</Link>
                    </DisabledPrompt>
                </FormRowView>
            </FormContainer>
        </td>
    </tr>
</CustomTableContent>
  );
}

